import { useCallback } from 'react';

import { ISubscriptionWellfunnel } from 'api/components/Subscriptions/types';

import {
    cancelSubscription,
    pauseSubscription,
    requestCancelSubscriptionWithOffer,
    requestResetSubscription,
    resumeSubscription,
    setCancellationCandidates,
    setReminder,
    updateSubscription,
} from 'store/subscriptions/actions';
import { selectSubscriptions } from 'store/subscriptions/selectors';

import { ONGOING_EXPERIMENTS } from 'constants/eppo.ts';
import { DISCOUNT_TYPES, SUBSCRIPTION_STATES } from 'constants/subscriptions';

import { sendAnalyticCancelSubscriptionClick } from 'services/Analytics';
import { useFeatureFlagWithAQA } from 'services/Eppo';

import { useDispatch, useSelector } from 'hooks';
import useModals from 'modals/hooks';
import { ModalName } from 'modals/types';

import { isMainSubscription } from 'helpers/subscriptions';

import { CancelFlowOffers, SubscriptionPauseType } from 'types/subscription';

import { getActiveSubscriptionIds, getSubscriptionIdsWithoutReminder } from './helpers';

const useCancelSubscriptionFlow = () => {
    const dispatch = useDispatch();

    const { cancellationCandidates, discountSubscription, subscriptions } = useSelector(selectSubscriptions);

    const { openModal } = useModals();

    const { value: cancelOfferAbVariant } = useFeatureFlagWithAQA<string>({
        flagKey: ONGOING_EXPERIMENTS.ReturnOfferAfterCancellation,
        defaultValue: 'default',
        type: 'string',
    });

    const initSubscriptionCancellation = useCallback(
        (candidate: ISubscriptionWellfunnel[]) => {
            const {
                external_id,
                state,
                pause: { pause_type },
                reminder,
                discount: { discount_type },
                product_type,
                is_promo_code_used,
            } = candidate[0];

            const isMain = isMainSubscription(product_type);

            if (!isMain || is_promo_code_used) {
                return dispatch(cancelSubscription({ cancellationCandidates: candidate, cancelOfferAbVariant }));
            }

            const isDiscountOfferAvailable =
                reminder || (pause_type && !(state === SUBSCRIPTION_STATES.ACTIVE)) || discount_type;

            openModal(
                isDiscountOfferAvailable ? ModalName.ConfirmationModalWithDiscount : ModalName.CancellationReasonModal
            );

            dispatch(setCancellationCandidates(candidate));
            sendAnalyticCancelSubscriptionClick({
                external_id,
                sub_status: state,
                free_month: pause_type === SubscriptionPauseType.FreeMonth && !(state === SUBSCRIPTION_STATES.ACTIVE),
                reminder,
                ...(candidate.length > 1 && { event_action: 'cancel_all_subscriptions' }),
            });
        },
        [cancelOfferAbVariant, dispatch, openModal]
    );

    const onCancelSubscription = useCallback(() => {
        if (!cancellationCandidates) return;

        dispatch(cancelSubscription({ cancellationCandidates, cancelOfferAbVariant }));
    }, [cancelOfferAbVariant, cancellationCandidates, dispatch]);

    const onChangeSubscription = useCallback(
        (isExtraOffer = false) => {
            if (!discountSubscription || !cancellationCandidates) return;

            dispatch(
                updateSubscription({
                    product: discountSubscription,
                    id: cancellationCandidates[0].external_id,
                    provider: cancellationCandidates[0].payment_provider,
                    discount_type: isExtraOffer ? DISCOUNT_TYPES.extra : DISCOUNT_TYPES.base,
                })
            );
        },
        [discountSubscription, dispatch, cancellationCandidates]
    );

    const onResetSubscription = useCallback(
        (isExtraOffer: boolean) => {
            if (!discountSubscription || !cancellationCandidates) return;

            dispatch(requestResetSubscription(isExtraOffer));
        },
        [discountSubscription, dispatch, cancellationCandidates]
    );

    const onResumeSubscription = useCallback(
        (external_id: string, date: string, provider: string) => {
            dispatch(resumeSubscription({ external_id, date, provider }));

            sendAnalyticCancelSubscriptionClick({
                external_id,
                sub_status: SUBSCRIPTION_STATES.Paused,
                event_action: CancelFlowOffers.Resume,
            });
        },
        [dispatch]
    );

    const onRequestPause = useCallback(
        (pauseType: SubscriptionPauseType) => {
            dispatch(
                pauseSubscription({
                    pause_type: pauseType,
                    externalIds: getActiveSubscriptionIds(cancellationCandidates ?? []),
                    provider: subscriptions[0].payment_provider,
                })
            );
        },
        [dispatch, cancellationCandidates, subscriptions]
    );

    const onRequestSetReminder = useCallback(() => {
        dispatch(
            setReminder({
                externalIds: getSubscriptionIdsWithoutReminder(cancellationCandidates ?? []),
                provider: subscriptions[0].payment_provider,
            })
        );
    }, [dispatch, cancellationCandidates, subscriptions]);

    const onCancelSubscriptionWithOffer = useCallback(() => {
        if (!cancellationCandidates) return;

        dispatch(requestCancelSubscriptionWithOffer(cancellationCandidates));
    }, [cancellationCandidates, dispatch]);

    return {
        initSubscriptionCancellation,
        cancellationCandidates,
        onCancelSubscription,
        onChangeSubscription,
        discountSubscription,
        onResumeSubscription,
        onRequestSetReminder,
        onRequestPause,
        onCancelSubscriptionWithOffer,
        onResetSubscription,
    };
};

export default useCancelSubscriptionFlow;
